<script setup lang="ts">
import { ref, computed ,onMounted, onUnmounted, watch} from 'vue';
import { LazyIcon } from '@/exports/components'

// 移除本地静态图标导入，统一由 nodeManager 提供

const searchText = ref('');
const selectedCategory = ref('');

// 拖拽状态管理
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });
let rafId = 0 as number;
let pendingPos: { x: number; y: number } | null = null;

// 面板元素引用与当前尺寸缓存（用于边界限制）
const rootEl = ref<HTMLElement | null>(null)
let currentPanelWidth = 300
let currentPanelHeight = 300

const emit = defineEmits(['update:nodeListShow', 'position-update']);

// 定义所有节点数�?
interface NodeItem {
  code: string;
  label: string;
  color: string;
  component?: string;
  componentType?: string;
  lib?: string;
  nodePath?: any;
  propertyPanel?: any;
  logicPath?: any;
  description?: string;
  bgColor?: string;
}

interface CategoryItem {
  code: string;
  label: string;
  color: string;
  items: NodeItem[];
}



const props = defineProps({

  nodeListShow:{
    type: Boolean,
    default: false,
    required: true
  },

  nodeManager:{
    type: Object,
    default: () => ({}),
    required: true
  }
});

// 本地响应式分类数据（通过事件刷新）
const categoriesRef = ref<CategoryItem[]>([])
function refreshCategories() {
  const categories = Array.from(props.nodeManager.getNodeCategories().entries()).map(([code, category]: [string, CategoryItem]) => {
    const filteredItems = (category.items || []).filter((item: any) => {
      const comp = (item && (item.component as string)) || ''
      const cfg = (props.nodeManager.getNodeConfig ? props.nodeManager.getNodeConfig(comp) : undefined)
      return cfg ? cfg.enabled !== false : true
    })
    return { ...category, items: filteredItems } as CategoryItem
  })
  categoriesRef.value = categories
}

onMounted(()=>{
  // ...
  // nodeManager 实例
  console.log(props.nodeManager,'nodeManager')
  // 初始加载分类
  refreshCategories()
  // 监听全局节点更新事件
  window.addEventListener('mflow:nodes-updated', refreshCategories)
})

onUnmounted(() => {
  window.removeEventListener('mflow:nodes-updated', refreshCategories)
})
// 当外部注入的 nodeManager 实例发生变化时，刷新分类
watch(() => props.nodeManager, () => {
  refreshCategories()
})

// 过滤后的节点列表（用于左侧分类）
const filteredCategories = computed(() => {
  const categories = categoriesRef.value
  if (!searchText.value) return categories;
  return categories.map((category: CategoryItem) => ({
    code: category.code,
    label: category.label,
    color: category.color,
    items: category.items.filter((item: NodeItem) =>
      item.label.toLowerCase().includes(searchText.value.toLowerCase())
    )
  } as CategoryItem)).filter((c: CategoryItem) => c.items.length > 0);
});

// 默认选中第一个分类
onMounted(() => {
  const first = filteredCategories.value[0];
  if (first) selectedCategory.value = first.code;
});

// 当前选中分类
const currentCategory = computed(() => {
  return filteredCategories.value.find(c => c.code === selectedCategory.value) || filteredCategories.value[0];
});

const currentCategoryName = computed(() => currentCategory.value ? currentCategory.value.label : '');
const currentCategoryColor = computed(() => currentCategory.value ? currentCategory.value.color : '#0089FF');
const currentCategoryItems = computed(() => currentCategory.value ? currentCategory.value.items : []);

const handleDragStart = (event: DragEvent, item: NodeItem, color: string) => {
  item.bgColor = color;
  // useEditorStore()?.dragNodeType = item;
  event.dataTransfer?.setData('node', JSON.stringify(item));
};

const handleDragEnd = (event: DragEvent) => {
  emit('update:nodeListShow', false);
  // drag end
};

// 面板拖拽逻辑（放入 <script setup> 内）
function onPanelMouseDown(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.node-header')) return;
  isDragging.value = true;
  const rect = (rootEl.value || (event.currentTarget as HTMLElement)).getBoundingClientRect();
  dragOffset.value = {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  };
  currentPanelWidth = rect.width
  currentPanelHeight = rect.height
  document.addEventListener('mousemove', onDocumentMouseMove);
  document.addEventListener('mouseup', onDocumentMouseUp);
  event.preventDefault();
}

function emitPosition(pos: { x: number; y: number }) {
  const margin = 4
  const x = Math.max(margin, Math.min(pos.x, window.innerWidth - currentPanelWidth - margin));
  const y = Math.max(margin, Math.min(pos.y, window.innerHeight - currentPanelHeight - margin));
  emit('position-update', { x, y });
}

function onDocumentMouseMove(event: MouseEvent) {
  if (!isDragging.value) return;
  const newX = event.clientX - dragOffset.value.x;
  const newY = event.clientY - dragOffset.value.y;
  pendingPos = { x: newX, y: newY };
  if (!rafId) {
    rafId = requestAnimationFrame(() => {
      if (pendingPos) emitPosition(pendingPos);
      pendingPos = null;
      rafId = 0;
    });
  }
}

function onDocumentMouseUp() {
  isDragging.value = false;
  document.removeEventListener('mousemove', onDocumentMouseMove);
  document.removeEventListener('mouseup', onDocumentMouseUp);
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
}

onUnmounted(() => {
  document.removeEventListener('mousemove', onDocumentMouseMove);
  document.removeEventListener('mouseup', onDocumentMouseUp);
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = 0;
  }
});

</script>

<template>
  <div 
    class="node-box" 
    v-show="nodeListShow"
    :class="{ 'is-dragging': isDragging }"
    ref="rootEl"
  >
    <div class="node-header" @mousedown="onPanelMouseDown" title="按住此处可拖动面板">
      <span class="drag-icon" aria-hidden="true"></span>
      <span class="drag-text">节点库</span>
    </div>
    <div class="node-search">
      <input type="text" v-model="searchText" placeholder="搜索节点库" />
    </div>
    <div class="node-content">
      <!-- 左侧分类列表 -->
      <div class="category-sidebar">
        <div 
          v-for="(category, cIdx) in filteredCategories" 
          :key="category.code || ('cat-' + cIdx)" 
          class="category-item"
          :class="{ active: selectedCategory === category.code }"
          @mouseenter="selectedCategory = category.code"
        >
          <span class="category-name">{{ category.label }}</span>
          <span class="category-arrow">›</span>
        </div>
      </div>
      
      <!-- 右侧节点列表 -->
      <div class="node-list">
        <div v-if="currentCategoryItems.length > 0" class="category-header">
          <span class="category-title">{{ currentCategoryName }}</span>
        </div>
        <div class="node-items">
          <div 
            v-for="(item, idx) in currentCategoryItems" 
            :key="item.code || (item.component + '-' + idx)" 
            class="node-item" 
            draggable="true" 
            @dragstart="handleDragStart($event, item, currentCategoryColor)" 
            @dragend="handleDragEnd($event)"
          >
            <div class="node-icon" :style="{ background: currentCategoryColor }">
              <LazyIcon :src="props.nodeManager.getIcon(item.component, item.componentType) || ''" :size="12" />
            </div>
            <div class="node-label" :title="item.label">{{ item.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.node-box {
  width: 300px;
  height: auto;
  max-height: 80vh;
  background-color: var(--Mflow-nodelist-bg);
  border-radius: 6px;
  color: var(--Mflow-nodelist-text);
  position: fixed; // 从 absolute 改为 fixed，定位参照视口，避免容器内边距/偏移影响
  z-index: 1250;   // 高于左/右/底部面板(1000)与内容，但不盖住顶部工具栏(1200+)，稍高一些更稳妥
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'Regular';
  
  // 拖拽中的光标提示
  &.is-dragging {
    cursor: grabbing;
  }

  .node-header {
    height: 28px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    border-bottom: 1px solid var(--Mflow-border-color);
    background: rgba(255,255,255,0.03);
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;

    .drag-icon {
      width: 14px; height: 14px; opacity: .7;
      background-image: radial-gradient(currentColor 1px, transparent 1px);
      background-size: 4px 4px;
      background-position: 0 0, 2px 2px;
      color: var(--Mflow-nodelist-title-color);
      border-radius: 2px;
      flex: 0 0 14px;
    }
    .drag-text { font-size: 11px; color: var(--Mflow-nodelist-title-color); }
  }

  &.is-dragging .node-header { cursor: grabbing; }

  .node-search {
    width: 100%;
    flex-shrink: 0;
    padding: 12px;
    position: sticky;
    top: 0;
    z-index: 10;
    input {
      width: 100%;
      height: 28px;
      background: var(--Mflow-nodelist-input-bg);
      border: 1px solid var(--Mflow-border-color);
      border-radius: 4px;
      padding: 0 12px 0 32px;
      color: var(--Mflow-nodelist-input-text);
      outline: none;
      transition: all 0.3s;
      font-family: 'Regular';
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%23ADADAB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>');
      background-repeat: no-repeat;
      background-position: 8px center;
      background-size: 16px;
      font-size: 11px;
      &:hover { border-color: var(--Mflow-boder-hover-color); }
      &:focus { border-color: var(--Mflow-primary-color); box-shadow: 0 0 0 2px var(--Mflow-primary-shadow-color); }
      &::placeholder { color: var(--Mflow-tip-text-color); }
    }
  }

  /* 双栏布局 */
  .node-content {
    display: grid;
    grid-template-columns: 110px 1fr;
    max-height: calc(100% - 56px);
    overflow: hidden;
  }

  /* 左侧分类栏 */
  .category-sidebar {
    border-right: 1px solid var(--Mflow-border-color);
    overflow-y: auto;
    padding: 8px 6px;

    &::-webkit-scrollbar { width: 4px; }

    .category-item {
      height: 28px;
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--Mflow-nodelist-title-color);
      cursor: pointer;
      padding: 0 8px;
      border-radius: 4px;
      transition: background .2s ease;

      &:hover { background: var(--Mflow-nodelist-item-hover); }
      &.active { background: rgba(255,255,255,0.06); color: #fff; }

      .category-name { font-size: 11px; }
      .category-arrow { opacity: .6; margin-left: auto; }
    }
  }

  /* 右侧节点列表区域 */
  .node-list {
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .category-header {
    flex-shrink: 0;
    padding: 10px 12px 6px;
    .category-title { font-size: 11px; font-weight: 500; color: var(--Mflow-nodelist-title-color); }
  }

  .node-items {
    flex: 1;
    overflow-y: auto;
    padding: 0 8px 8px 8px;

    &::-webkit-scrollbar { width: 4px; }

    .node-item {
      width: 100%;
      height: 24px;
      display: flex;
      align-items: center;
      border-radius: 4px;
      padding: 0 6px;
      cursor: grab;
      transition: background .2s ease;

      &:hover { background: var(--Mflow-nodelist-item-hover); }

      .node-icon {
        width: 16px; height: 16px; margin-right: 8px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 2px; background: #0089FF; overflow: hidden;
        /* 移除固定 img 尺寸，交由 LazyIcon 的 size 控制 */
      }
      .node-label { font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1; }
    }
  }
}
</style>
